import React, {ReactElement, useEffect, useState, Suspense} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {registerUser} from '../../store/userActions';
import {isValidEmail} from '../../lib/validators';
import {AsFields} from '../../lib/constants';
import {useHistory} from 'react-router-dom';
const Register = React.lazy(()=> import('./Register'));
import {getCurrentDefaultUserNotificationSetting, updateUserNotificationSetting} from '../../api';
import * as Sentry from '@sentry/react';
// import publicIp from "public-ip"
// import { IAppState } from 'store/store'
// import {selectCurrentUserName} from '../../store/userSelectors';


const RegisterContainer: React.FC = (): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();

  // const username = useSelector((state: any) => selectCurrentUserName(state));

  const emptyReg = {
    firstName: '',
    lastName: '',
    email: '',
    email2: '',
    birthDate: '',
    password: '',
    password2: '',
    agreedToTerms: false,
  };

  const [registration, setRegistration] = useState<AsFields>(emptyReg);
  const [errorText, setErrorText] = useState<{ [key: string]: string; }>({});
  const [emailAlreadyExists, setEmailAlreadyExists] = useState<boolean>(false);
  const [validUniqueEmail, setValidUniqueEmail] = useState<boolean>(false);
  const [checkRegister, setCheckRegister] = useState<boolean>(false);
  const [formReady, setFormReady] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [httpCode, setHttpCode] = useState(0);
  const [dialogMsg, setDialogMsg] = useState('There was an error creating your account. Please contact us if this continues to be a problem.');
  const [dialogTitle, setDialogTitle] = useState('Error');

  // useEffect(() => {
  //   if (username) {
  //     history.push('/');
  //   }
  // }, [username, history]);

  useEffect(() => {
    const setEmail = async (): Promise<void> => {
      const validEmail = isValidEmail(registration.email);
      const availableEmailCheck = true;
      if (registration.email !== registration.email2) {
        setErrorText({...errorText, email2: 'Emails do not match'});
      } else {
        if (registration.email.length >=1 && !validEmail) {
          setErrorText({...errorText, email: 'Invalid email'});
          setEmailAlreadyExists(false);
        } else if (!availableEmailCheck) {
          setErrorText({...errorText, email: 'There is already an account with that email'});
          setEmailAlreadyExists(true);
        }

        setValidUniqueEmail(validEmail && availableEmailCheck);
      }
    };
    setEmail();
  }, [errorText, registration.email, registration.email2]); // errorText should not be included (causes infinite rerender)

  /**
   * Set the current user notification settings to default values
   *
   * @param {number} userId
   *
   * @return {Promise<void>}
  */
  const setUserToDefaultNotificationSettings = async (userId) => {
    const publicPage = true;
    const currentDefaultValuesResponse = await getCurrentDefaultUserNotificationSetting(publicPage);
    if (currentDefaultValuesResponse?.id) {
      try {
        const resetUserNotificationSettingResponse = await updateUserNotificationSetting(userId, currentDefaultValuesResponse, publicPage);
      } catch (e) {
        console.error(e);
      }
    }
  };


  const submitForm: any = async (event: any) => {
    setCheckRegister(true);
    event.preventDefault();
    if (formReady) {
      setIsLoading(true);
      try {
        // Set Sentry User
        Sentry.setUser({email: registration.email});
      } catch (error) {
        console.error('error setting Sentry User: ', error);
      }
      const register = registerUser(registration);
      try {
        setDialogMsg('There was an error creating your account. Please contact us if this continues to be a problem.');
        const [reg, user] = await register(dispatch);
        const setDefaultResponse = await setUserToDefaultNotificationSettings(user.id);
        if (registration) {
          history.push('/');
        } else {
          history.push('/');
        }
      } catch (e) {
        // @ts-ignore
        setHttpCode(await e.response.status);
        // @ts-ignore
        if (await e.response.status === 429 || await e.response.status === 400) {
          // @ts-ignore
          if (await e.response.data.message[0].messages[0].message === 'Your account email is not confirmed') {
            setDialogMsg('Please check your inbox for the verification email. If you do not see it, please check your spam folder. You must click the verification link to continue login.');
            setDialogTitle(`Please Verify Your Email`);
          } else {
          // @ts-ignore
            setDialogMsg(await e.response.data.message[0].messages[0].message);
            setDialogTitle(`Status: ${httpCode} - Registration Error`);
          }
        }
        setDialogOpen(true);
        console.error(e);
      }
    }
    setIsLoading(false);
  };

  return (
    <Suspense fallback={<div>Loading Registration Form...</div>}>
      <Register
        registration={registration}
        setRegistration={setRegistration}
        setErrorText={setErrorText}
        errorText={errorText}
        setFormReady={setFormReady}
        validUniqueEmail={validUniqueEmail}
        checkRegister={checkRegister}
        emailAlreadyExists={emailAlreadyExists}
        setCheckRegister={setCheckRegister}
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        httpCode={httpCode}
        dialogMsg={dialogMsg}
        dialogTitle={dialogTitle}
        isLoading={isLoading}
        submitForm={submitForm} />
    </Suspense>
  );
};

export default RegisterContainer;
